import { useQuery } from "@tanstack/react-query";
import { FileText, Building2, User, Clock } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PlatformLayout } from "./platform-layout";

interface AuditLogEntry {
  id: string;
  action: string;
  actorPlatformUserId: string;
  actorEmail: string;
  targetWorkspaceId: string | null;
  targetWorkspaceName: string | null;
  targetUserId: string | null;
  targetUserEmail: string | null;
  metadataJson: string | null;
  createdAt: string;
}

const actionLabels: Record<string, { label: string; color: string }> = {
  workspace_created: { label: "Workspace Created", color: "bg-green-500" },
  workspace_tier_changed: { label: "Tier Changed", color: "bg-blue-500" },
  workspace_activated: { label: "Activated", color: "bg-green-500" },
  workspace_deactivated: { label: "Deactivated", color: "bg-yellow-500" },
  workspace_locked: { label: "Locked", color: "bg-red-500" },
  workspace_unlocked: { label: "Unlocked", color: "bg-green-500" },
  permission_override_created: { label: "Override Created", color: "bg-purple-500" },
  permission_override_deleted: { label: "Override Deleted", color: "bg-slate-500" },
  impersonation_started: { label: "Impersonation Started", color: "bg-amber-500" },
  impersonation_ended: { label: "Impersonation Ended", color: "bg-slate-500" },
  platform_user_created: { label: "Admin Created", color: "bg-green-500" },
  platform_user_deactivated: { label: "Admin Deactivated", color: "bg-red-500" },
  tier_created: { label: "Tier Created", color: "bg-green-500" },
  tier_updated: { label: "Tier Updated", color: "bg-blue-500" },
  tier_deleted: { label: "Tier Deleted", color: "bg-red-500" },
  workspace_soft_deleted: { label: "Workspace Soft-Deleted", color: "bg-yellow-500" },
  workspace_hard_deleted: { label: "Workspace Hard-Deleted", color: "bg-red-700" },
  seed_failure_retry_triggered: { label: "Seed Retry Triggered", color: "bg-amber-500" },
  // Two-person approval workflow chain (task #608). Each row in the
  // chain shares an `approvalRequestId` in metadata so an admin can
  // visually trace request → approve → execute (or expired/failed).
  approval_request_created: { label: "Approval Requested", color: "bg-amber-500" },
  approval_request_approved: { label: "Approval Granted", color: "bg-green-600" },
  approval_request_rejected: { label: "Approval Rejected", color: "bg-red-500" },
  approval_request_expired: { label: "Approval Expired", color: "bg-slate-500" },
  approval_request_executed: { label: "Action Executed", color: "bg-blue-600" },
  approval_request_execution_failed: { label: "Execution Failed", color: "bg-red-700" },
  // Task #8 — every fire of a `platformOnly` notification category writes
  // one row here so platform admins have an authoritative trail of the
  // events workspace UIs never see (backup_failed, sbom_vulnerability,
  // honeytoken_touched, etc.). The originating category is on metadata.
  notification_hub_platform_event: { label: "Platform Notification", color: "bg-indigo-500" },
};

export function AuditContent() {
  const { data: logs, isLoading } = useQuery<AuditLogEntry[]>({
    queryKey: ["/api/platform/audit-log"],
  });

  return (
    <>
      <div className="p-3 sm:p-6 space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Audit Log</h1>
          <p className="text-muted-foreground">
            Immutable record of all platform administration actions
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
          </div>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Recent Activity
              </CardTitle>
              <CardDescription>
                Showing the last 100 audit log entries
              </CardDescription>
            </CardHeader>
            <CardContent>
              {logs?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No audit log entries yet
                </p>
              ) : (
                <div className="space-y-3">
                  {logs?.map((log) => {
                    const actionInfo = actionLabels[log.action] || { 
                      label: log.action, 
                      color: "bg-slate-500" 
                    };
                    const metadata = log.metadataJson ? JSON.parse(log.metadataJson) : null;

                    return (
                      <div 
                        key={log.id}
                        className="flex items-start gap-4 p-3 rounded-lg border"
                        data-testid={`audit-entry-${log.id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={actionInfo.color}>
                              {actionInfo.label}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              by {log.actorEmail}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            {log.targetWorkspaceName && (
                              <span className="flex items-center gap-1">
                                <Building2 className="h-3 w-3" />
                                {log.targetWorkspaceName}
                              </span>
                            )}
                            {log.targetUserEmail && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {log.targetUserEmail}
                              </span>
                            )}
                          </div>

                          {metadata && (
                            <div className="mt-2 text-xs text-muted-foreground bg-slate-100 dark:bg-slate-800 rounded p-2">
                              {Object.entries(metadata).map(([key, value]) => (
                                <span key={key} className="mr-3">
                                  {key}: <span className="font-medium">{String(value)}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {new Date(log.createdAt).toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}

export default function PlatformAudit() {
  return <PlatformLayout><AuditContent /></PlatformLayout>;
}
